import FiltersModal from "components/layout/MobileDialogs/FiltersModal/FiltersModal";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import UserPreferences from "../../../../helpers/UserPreferences";
import scss from "./Controls.module.scss";
import { useTranslation } from "react-i18next";
import { Menu, MenuItem } from "@mui/material";
import useAPI from "hooks/useAPI";
import useDefaultProductSort, { USER_SORT_STORAGE_KEY } from "hooks/useDefaultProductSort";
import ProductNavigation from "components/products/ProductNavigation";

/**
 * Maps a `field___direction` sort option id (as managed in CMS Params, group
 * `product_sort_options_b2b`) to the translation key suffix used for its label.
 * An id without an entry here falls back to the label sent by the backend.
 */
const SORT_OPTION_TRANSLATION_KEYS = {
    new___desc: "new",
    price___asc: "by_price_ascending",
    price___desc: "by_price_descending",
    name___asc: "by_name_ascending",
    name___desc: "by_name_descending",
    inventory___asc: "in_lager_ascending",
    inventory___desc: "in_lager_descending",
};

/** Shown while the sort options are loading from the backend, or if that request fails. */
const FALLBACK_SORT_OPTIONS = [
    { id: "new___desc", name: "Novo" },
    { id: "price___asc", name: "Po ceni, rastuće" },
    { id: "price___desc", name: "Po ceni, opadajuće" },
    { id: "name___asc", name: "Po nazivu, rastuće" },
    { id: "name___desc", name: "Po nazivu, opadajuće" },
    { id: "inventory___asc", name: "Na stanju, rastuće" },
    { id: "inventory___desc", name: "Na stanju, opadajuće" },
];

const SortIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M3.5 5h9M3.5 10h7M3.5 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M16 4.5v11m-2.5-2.5L16 15.5l2.5-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PageSizeIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 7.5h14M6.5 11h7M6.5 14h4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const GridViewIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="2.75" y="2.75" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="11.25" y="2.75" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="2.75" y="11.25" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="11.25" y="11.25" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);

const ListViewIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="3.5" cy="4.5" r="1" fill="currentColor" />
        <circle cx="3.5" cy="10" r="1" fill="currentColor" />
        <circle cx="3.5" cy="15.5" r="1" fill="currentColor" />
        <path d="M7 4.5h10M7 10h10M7 15.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const MoreOptionsIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="4" r="1.5" fill="currentColor" />
        <circle cx="10" cy="10" r="1.5" fill="currentColor" />
        <circle cx="10" cy="16" r="1.5" fill="currentColor" />
    </svg>
);

const DropdownControl = ({ label, value, options, onChange, icon, className = "" }) => {
    const [anchor, setAnchor] = useState(null);
    const selectedOption = options.find((option) => String(option.value) === String(value));
    const selectedLabel = selectedOption?.label ?? "";
    const accessibleLabel = selectedLabel.startsWith(`${label}:`) ? selectedLabel : `${label}: ${selectedLabel}`;

    return (
        <div className={`${scss.dropdown} ${className}`}>
            <button
                type="button"
                className={scss.dropdownTrigger}
                aria-label={accessibleLabel}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchor)}
                onClick={(event) => setAnchor(event.currentTarget)}
            >
                <span className={scss.dropdownIcon}>{icon}</span>
                <span className={scss.dropdownValue}>{selectedOption?.label ?? options[0]?.label}</span>
                <svg className={scss.chevron} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ className: scss.dropdownPaper, style: { width: anchor?.offsetWidth, maxWidth: anchor?.offsetWidth } }}
                MenuListProps={{ "aria-label": label }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        className={scss.dropdownOption}
                        selected={String(option.value) === String(value)}
                        onClick={() => {
                            setAnchor(null);
                            onChange(option.value);
                        }}
                    >
                        <span>{option.label}</span>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

/**
 * Show controls for the presentation of the products.
 *
 * @param {?string} title The optional title to show, or null to hide the controls.
 * @param {function({ limit: int, view: string, order: string })} onParamChange Invoked when any of the parameters change.
 *
 * @return {JSX.Element}
 * @constructor
 */

const Controls = ({
    showFilters = true,
    title,
    onParamChange,
    filters,
    setActiveFilters,
    numberOfProducts,
    setSort,
    categoryid,
    sort,
    setLimit,
    limit,
    setCurrentPage,
    changeFilters,
    isFetched,
    lastSelectedFilterKey,
    setChangeFilters,
    setLastSelectedFilterKey,
    newQuery,
    setNewQuery,
    selectedFilters,
    availableFilters,
    setSelectedFilters,
    translateKey,
}) => {
    const { t } = useTranslation();
    const api = useAPI();
    const { secondary: defaultSecondarySort } = useDefaultProductSort();

    const [openFilters, setOpenFilters] = useState(false);

    // Send updated values to the parent
    const updateParent = () => onParamChange && onParamChange({ ready: true, ...UserPreferences.get("presentation") });
    useEffect(updateParent, []);
    // Local state: The number of products to show
    // const [limit, setLimit] = useState(UserPreferences.get("presentation.limit"));
    // useEffect(() => UserPreferences.set("presentation.limit", limit) && updateParent(), [limit]);

    // Local state: The order of the products
    const [order, setOrder] = useState(UserPreferences.get("presentation.order"));
    useEffect(() => UserPreferences.set("presentation.order", order) && updateParent(), [order]);

    const [view, setView] = useState(UserPreferences.get("presentation.view"));
    useEffect(() => UserPreferences.set("presentation.view", view) && updateParent(), [view]);

    const [optionsAnchor, setOptionsAnchor] = useState(null);
    const handlePrint = () => {
        setOptionsAnchor(null);
        window.print();
    };

    // The available "order by" options, sourced from CMS Params (group `product_sort_options_b2b`,
    // the same list managed under "Podrazumevano sortiranje proizvoda u listi").
    const { data: sortOptionsData } = useQuery(["productSortOptions"], () => api.get("/options/global/product-sort-options"), {
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
    });

    const optionsArray = (sortOptionsData ?? FALLBACK_SORT_OPTIONS).map((option) => {
        const [field, direction] = option.id.split("___");
        const translationKeySuffix = SORT_OPTION_TRANSLATION_KEYS[option.id];
        return {
            value: `${field},${direction}`,
            label: translationKeySuffix ? t(`${translateKey}-controls-${translationKeySuffix}`) : option.name,
        };
    });

    const selectedSort = sort?.[sort.length - 1];
    const selectedSortValue = `${selectedSort?.field ?? ""},${selectedSort?.direction ?? ""}`;

    const limitLabel = "Prikaži";
    const limitArray = [4, 8, 12, 18, 24].map((value) => ({ value, label: `${limitLabel}: ${value}` }));
    return (
        <>
            <div className={`${scss.wrapper} col-12`}>
                {title && (
                    <div className={scss.heading}>
                        <span className={scss.title}>{title}</span>
                    </div>
                )}
                <ProductNavigation count={numberOfProducts ?? 0} />
                <div className={scss.presentationControl}>
                    {showFilters && (
                        <div className={scss.filters}>
                            <button type="button" className={scss.filtersButton} aria-haspopup="dialog" aria-expanded={openFilters} onClick={() => setOpenFilters(!openFilters)}>
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path d="M3 4.5h14l-5.5 6v4.5l-3 1.5v-6L3 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                                </svg>
                                <span>{t(`${translateKey}-controls-filters`)}</span>
                            </button>
                        </div>
                    )}
                    <DropdownControl
                        label={t(`${translateKey}-controls-sort`, { defaultValue: "Sortiranje" })}
                        value={selectedSortValue}
                        options={optionsArray}
                        icon={<SortIcon />}
                        className={scss.sortDropdown}
                        onChange={(value) => {
                            const [field, direction] = value.split(",");
                            localStorage.setItem(USER_SORT_STORAGE_KEY, `${field}___${direction}`);
                            setSort([...defaultSecondarySort, { field, direction }]);
                            setCurrentPage(1);
                        }}
                    />
                    <DropdownControl
                        label={limitLabel}
                        value={limit}
                        options={limitArray}
                        icon={<PageSizeIcon />}
                        className={scss.limitDropdown}
                        onChange={(value) => {
                            setLimit(String(value));
                            setCurrentPage(1);
                        }}
                    />
                    <div className={scss.viewSwitch} role="group" aria-label="Prikaz proizvoda">
                        <button type="button" className={`${scss.iconButton} ${view === ProductsView.Grid ? scss.activeIcon : ""}`} aria-label="Mrežni prikaz" aria-pressed={view === ProductsView.Grid} onClick={() => setView(ProductsView.Grid)}>
                            <GridViewIcon />
                            <span>Mreža</span>
                        </button>
                        <button type="button" className={`${scss.iconButton} ${view === ProductsView.List ? scss.activeIcon : ""}`} aria-label="Prikaz liste" aria-pressed={view === ProductsView.List} onClick={() => setView(ProductsView.List)}>
                            <ListViewIcon />
                            <span>Lista</span>
                        </button>
                    </div>
                    <div className={scss.optionsControl}>
                        <button type="button" className={scss.optionsButton} aria-label="Dodatne opcije" aria-haspopup="menu" aria-expanded={Boolean(optionsAnchor)} onClick={(event) => setOptionsAnchor(event.currentTarget)}>
                            <MoreOptionsIcon />
                        </button>
                        <Menu anchorEl={optionsAnchor} open={Boolean(optionsAnchor)} onClose={() => setOptionsAnchor(null)} PaperProps={{ className: scss.dropdownPaper }}>
                            <MenuItem className={scss.dropdownOption} onClick={handlePrint}>{t(`${translateKey}-controls-print`)}</MenuItem>
                        </Menu>
                    </div>
                </div>
            </div>
            <FiltersModal
                openModal={openFilters}
                setOpenModal={setOpenFilters}
                filters={filters}
                setActiveFilters={setActiveFilters}
                changeFilters={changeFilters}
                isFetched={isFetched}
                lastSelectedFilterKey={lastSelectedFilterKey}
                setChangeFilters={setChangeFilters}
                setLastSelectedFilterKey={setLastSelectedFilterKey}
                newQuery={newQuery}
                selectedFilters={selectedFilters}
                availableFilters={availableFilters}
                setSelectedFilters={setSelectedFilters}
                setCurrentPage={setCurrentPage}
                categoryid={categoryid}
                setNewQuery={setNewQuery}
            />
        </>
    );
};

/** The list of possible views for the list of products. */
export const ProductsView = {
    Grid: "grid",
    List: "list",
};

/** The list of possible number of products per page. */
export const ProductsLimit = {
    _4: 4,
    _8: 8,
    _12: 12,
    _18: 18,
    _24: 24,
};

/** The list of possible order variations. */
export const ProductsOrder = {
    ASC: "asc",
    DESC: "desc",
};

/**
 * The default controls for the presentation of products.
 *
 * @typedef PresentationControls
 *      @property {int} limit
 *      @property {string} view
 *      @property {string} order
 */
export const DefaultProductsControls = {
    page: 1,
    view: ProductsView.Grid,
    limit: ProductsLimit._8,
    order: ProductsOrder.DESC,
};

export default Controls;
